const STORAGE_KEY = "aaalife_ad_personalization_opt_out";
const RESPECT_GLOBAL_PRIVACY_CONTROL = true;

let hasInitializedPersonalizedAdOptout = false;

export function initPersonalizedAdOptout() {
    if (hasInitializedPersonalizedAdOptout) {
        return;
    }

    const openButton = document.getElementById("aa-open-adpref-modal");
    const modal = document.getElementById("aa-adpref-modal");
    const dialog = modal ? modal.querySelector(".aa-privacy-modal__dialog") : null;
    const title = document.getElementById("aa-adpref-modal-title");
    const closeButton = document.getElementById("aa-adpref-modal-close");
    const cancelButton = document.getElementById("aa-adpref-cancel");
    const saveButton = document.getElementById("aa-adpref-save");
    const switchButton = document.getElementById("aa-adpref-switch");
    const switchState = document.getElementById("aa-adpref-switch-state");
    const modalStatus = document.getElementById("aa-adpref-modal-status");
    const footerStatus = document.getElementById("aa-adpref-footer-status");
    const gpcMessage = document.getElementById("aa-adpref-gpc-message");

    if (
        !openButton ||
        !modal ||
        !dialog ||
        !title ||
        !closeButton ||
        !cancelButton ||
        !saveButton ||
        !switchButton ||
        !switchState ||
        !modalStatus ||
        !footerStatus ||
        !gpcMessage
    ) {
        return;
    }

    hasInitializedPersonalizedAdOptout = true;

    let lastFocusedElement = null;
    let pendingOptOutState = false;

    initializeGoogleConsentShim();
    initializeModalAccessibilityAttributes();
    initializeFooterStatus();

    openButton.addEventListener("click", openModal);
    closeButton.addEventListener("click", closeModal);
    cancelButton.addEventListener("click", closeModal);
    saveButton.addEventListener("click", savePreferenceAndClose);

    modal.addEventListener("click", function (event) {
        if (event.target && event.target.hasAttribute("data-aa-adpref-close")) {
            closeModal();
        }
    });

    switchButton.addEventListener("click", function () {
        if (hasGlobalPrivacyControlOptOut()) {
            pendingOptOutState = true;
            updateSwitchVisualState(true);
            return;
        }

        pendingOptOutState = !pendingOptOutState;
        updateSwitchVisualState(pendingOptOutState);
    });

    function initializeGoogleConsentShim() {
        window.dataLayer = window.dataLayer || [];

        window.gtag =
            window.gtag ||
            function () {
                window.dataLayer.push(arguments);
            };
    }

    function initializeModalAccessibilityAttributes() {
        openButton.setAttribute("aria-haspopup", "dialog");
        openButton.setAttribute("aria-controls", "aa-adpref-modal");
        openButton.setAttribute("aria-expanded", "false");

        if (!title.hasAttribute("tabindex")) {
            title.setAttribute("tabindex", "-1");
        }

        if (!dialog.hasAttribute("tabindex")) {
            dialog.setAttribute("tabindex", "-1");
        }
    }

    function hasGlobalPrivacyControlOptOut() {
        try {
            return RESPECT_GLOBAL_PRIVACY_CONTROL && window.navigator && window.navigator.globalPrivacyControl === true;
        } catch (e) {
            return false;
        }
    }

    function getStoredOptOutState() {
        try {
            return localStorage.getItem(STORAGE_KEY) === "true";
        } catch (e) {
            return false;
        }
    }

    function getEffectiveOptOutState() {
        return getStoredOptOutState() || hasGlobalPrivacyControlOptOut();
    }

    function saveStoredOptOutState(isOptedOut) {
        try {
            localStorage.setItem(STORAGE_KEY, isOptedOut ? "true" : "false");
        } catch (e) {
            /*
                If localStorage is unavailable, still update Google consent
                for the current pageview.
            */
        }
    }

    function updateAdvertisingPrivacySignals(isOptedOut) {
        const adPersonalizationValue = isOptedOut ? "denied" : "granted";
        const adUserDataValue = isOptedOut ? "denied" : "granted";
        const microsoftAdStorageValue = isOptedOut ? "denied" : "granted";

        /*
            Google Consent Mode v2.
        */
        window.gtag("consent", "update", {
            ad_personalization: adPersonalizationValue,
            ad_user_data: adUserDataValue,
        });

        /*
            Legacy / additional Google Ads personalization control.
            Helpful for Google Ads tags that still reference this setting.
        */
        window.gtag("set", "allow_ad_personalization_signals", !isOptedOut);

        /*
            Microsoft Ads UET Consent Mode.
        */
        window.uetq = window.uetq || [];
        window.uetq.push("consent", "update", {
            ad_storage: microsoftAdStorageValue,
        });

        /*
            Optional Microsoft Clarity Consent V2.
            Only use this if Clarity is installed.
            This keeps analytics storage granted but denies ad storage when opted out.
        */
        if (typeof window.clarity === "function") {
            window.clarity("consentv2", {
                ad_Storage: isOptedOut ? "denied" : "granted",
                analytics_Storage: "granted",
            });
        }

        /*
            General GTM event for Google Ads RDP and other vendor controls.
        */
        window.dataLayer.push({
            event: "privacy_optout_update",
            privacy_opt_out_targeted_ads: isOptedOut,
            ad_personalization: adPersonalizationValue,
            ad_user_data: adUserDataValue,
            microsoft_uet_ad_storage: microsoftAdStorageValue,
            restricted_data_processing: isOptedOut,
        });
    }

    function getFocusableElements() {
        return Array.prototype.slice
            .call(
                dialog.querySelectorAll(
                    [
                        "a[href]",
                        "button:not([disabled])",
                        "textarea:not([disabled])",
                        "input:not([disabled])",
                        "select:not([disabled])",
                        "[tabindex]:not([tabindex='-1'])",
                    ].join(","),
                ),
            )
            .filter(isElementVisible);
    }

    function isElementVisible(element) {
        const style = window.getComputedStyle(element);

        return style.visibility !== "hidden" && style.display !== "none" && element.getClientRects().length > 0;
    }

    function updateSwitchVisualState(isOptedOut) {
        const gpcEnabled = hasGlobalPrivacyControlOptOut();
        const effectiveState = isOptedOut || gpcEnabled;

        switchButton.setAttribute("aria-checked", effectiveState ? "true" : "false");
        switchState.textContent = effectiveState ? "On" : "Off";

        if (gpcEnabled) {
            switchButton.setAttribute("aria-disabled", "true");
            gpcMessage.hidden = false;
            modalStatus.textContent =
                "Targeted advertising opt-out is on because your browser is sending a Global Privacy Control signal.";
        } else {
            switchButton.removeAttribute("aria-disabled");
            gpcMessage.hidden = true;
            modalStatus.textContent = effectiveState
                ? "Targeted advertising opt-out is on. Choose Save preference to keep this setting."
                : "Targeted advertising opt-out is off. Choose Save preference to keep this setting.";
        }
    }

    function openModal() {
        if (!modal.hidden) {
            return;
        }

        lastFocusedElement = document.activeElement;
        pendingOptOutState = getEffectiveOptOutState();

        updateSwitchVisualState(pendingOptOutState);

        modal.hidden = false;
        openButton.setAttribute("aria-expanded", "true");
        document.body.classList.add("aa-privacy-modal-is-open");

        /*
            Focus the modal title first so screen-reader users hear the context
            before interacting with the switch.
        */
        window.setTimeout(function () {
            title.focus();
        }, 0);

        document.addEventListener("keydown", handleDocumentKeydown);
    }

    function closeModal() {
        if (modal.hidden) {
            return;
        }

        modal.hidden = true;
        openButton.setAttribute("aria-expanded", "false");
        document.body.classList.remove("aa-privacy-modal-is-open");
        document.removeEventListener("keydown", handleDocumentKeydown);

        if (lastFocusedElement && typeof lastFocusedElement.focus === "function") {
            lastFocusedElement.focus();
        }
    }

    function savePreferenceAndClose() {
        const gpcEnabled = hasGlobalPrivacyControlOptOut();
        const finalOptOutState = gpcEnabled ? true : pendingOptOutState;

        saveStoredOptOutState(finalOptOutState);
        updateAdvertisingPrivacySignals(finalOptOutState);

        footerStatus.textContent = finalOptOutState
            ? "Targeted advertising opt-out is turned on for this browser."
            : "Targeted advertising opt-out is turned off for this browser.";

        closeModal();
    }

    function handleDocumentKeydown(event) {
        if (modal.hidden) {
            return;
        }

        if (event.key === "Escape") {
            event.preventDefault();
            closeModal();
            return;
        }

        if (event.key !== "Tab") {
            return;
        }

        trapFocusInsideModal(event);
    }

    function trapFocusInsideModal(event) {
        const focusableElements = getFocusableElements();

        if (focusableElements.length === 0) {
            event.preventDefault();
            dialog.focus();
            return;
        }

        const firstElement = focusableElements[0];
        const lastElement = focusableElements[focusableElements.length - 1];
        const activeElement = document.activeElement;

        /*
            The modal title has tabindex="-1" and receives initial focus.
            If a user presses Shift+Tab from the title, keep focus in the modal.
        */
        if (event.shiftKey && activeElement === title) {
            event.preventDefault();
            lastElement.focus();
            return;
        }

        if (event.shiftKey && activeElement === firstElement) {
            event.preventDefault();
            lastElement.focus();
            return;
        }

        if (!event.shiftKey && activeElement === lastElement) {
            event.preventDefault();
            firstElement.focus();
            return;
        }

        /*
            If focus somehow escapes the dialog while it is open,
            bring it back into the modal.
        */
        if (!dialog.contains(activeElement)) {
            event.preventDefault();
            firstElement.focus();
        }
    }

    function initializeFooterStatus() {
        /*
            Initialize the footer status for assistive tech without sending
            a Google update here. The pre-GTM head snippet handles saved
            opt-outs before GTM / GA4 loads.
        */
        footerStatus.textContent = getEffectiveOptOutState()
            ? "Targeted advertising opt-out is turned on for this browser."
            : "Targeted advertising opt-out is turned off for this browser.";
    }
}
